<template>
  <div
    :class="[typeDetails.color, isVertical ? 'tw:items-start' : 'tw:items-center']"
    class="tw:relative tw:p-3 tw:flex tw:gap-2 tw:rounded-md tw:h-fit tw:w-full tw:transition-colors tw:duration-500"
  >
    <div
      v-if="shouldShowIcon"
      :class="[
        { 'tw:text-blue-500': type === 'tip' },
        { 'tw:text-green-500': type === 'success' },
        { 'tw:text-red-500': type === 'error' },
        { 'tw:text-default-900': type === 'info' },
        { 'tw:text-yellow-500': type === 'caution' },
        { 'tw:text-violet-500': type === 'ai' },
        { 'tw:text-purple-500': type === 'purple' },
        { 'tw:text-orange-500': type === 'orange' },
      ]"
      class="tw:w-4 tw:flex tw:justify-center tw:items-center tw:text-center tw:h-lh"
    >
      <!-- @slot To use custom icon -->
      <slot name="custom-icon">
        <FontAwesomeIcon :key="type" :icon="typeDetails.icon" class="tw:size-4" />
      </slot>
    </div>

    <div
      :class="isVertical ? 'tw:flex-col  tw:items-start' : 'tw:flex-row  tw:items-center'"
      class="tw:flex tw:flex-nowrap tw:gap-2 tw:w-full tw:justify-between"
    >
      <div v-if="title || desc" class="tw:flex tw:flex-col tw:gap-1">
        <p v-if="title" class="tw:body-2--bold tw:text-black">
          {{ title }}
        </p>
        <p v-if="desc" class="tw:body-2 tw:text-default-700">
          {{ desc }}
        </p>
      </div>
      <!-- @slot DEPRECATED. For links. Use slot `custom-content` -->
      <slot name="default" />
      <!-- @slot Can be used for the entire content of the component. Can mix with `title` and `desc` props. If used with props, this slot will render below or on the right site of the existing content (depends of orientation property). -->
      <slot name="custom-content" />
    </div>

    <div v-if="showCloseIcon" class="tw:justify-self-end tw:ml-auto">
      <EdroneButtonIcon size="small" type="plain" @click="$emit('close')">
        <FontAwesomeIcon icon="fa-light fa-xmark" />
      </EdroneButtonIcon>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, useSlots } from "vue";
import EdroneButtonIcon from "edrone-components/EdroneButtonIcon.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

interface Emits {
  (event: "close"): void;
}

export type EdroneAlertType = "tip" | "success" | "error" | "info" | "caution" | "ai" | "purple" | "orange";

interface Props {
  title?: string;
  desc?: string;
  type?: EdroneAlertType;
  showCloseIcon?: boolean;
  showIcon?: boolean;
  isVertical?: boolean;
}

defineEmits<Emits>();
const props = withDefaults(defineProps<Props>(), {
  title: "",
  desc: "",
  type: "error",
  showCloseIcon: false,
  showIcon: true,
  isVertical: true,
});

const slots = useSlots();
const shouldShowIcon = computed(() => props.showIcon && (typeDetails.value.icon || slots["custom-icon"]));

const typeDetails = computed(() => {
  const details: Record<string, { color: string; icon: string }> = {
    tip: { color: "tw:bg-blue-100", icon: "fa-light fa-lightbulb-on" },
    success: { color: "tw:bg-green-100", icon: "fa-light fa-circle-check" },
    error: { color: "tw:bg-red-100", icon: "fa-light fa-octagon-exclamation" },
    info: { color: "tw:bg-default-100", icon: "fa-light fa-circle-check" },
    caution: { color: "tw:bg-yellow-100", icon: "fa-light fa-triangle-exclamation" },
    ai: { color: "tw:bg-violet-100", icon: "fa-solid fa-sparkles" },
    purple: { color: "tw:bg-purple-100", icon: "fa-solid fa-fire" },
    orange: { color: "tw:bg-orange-100", icon: "fa-light fa-circle-question" },
  };
  return details[props.type] || details.error;
});
</script>
